import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store';
import { LogOut, User, ChevronDown } from 'lucide-react';
import { logout } from '@/store/slices/authSlice';
import type { RootState } from '@/store';
import { BASE_URL } from '@/config';

export default function AccountPopover() {
  const [open, setOpen] = useState(false);
  const user = useAppSelector((s: RootState) => s.auth.user);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    dispatch(logout());
    setOpen(false);
    navigate(`${BASE_URL}login`);
  };

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  const displayName = user?.username ?? user?.login ?? user?.nom ?? 'Compte';

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:ring-offset-2"
      >
        <span className="max-w-[120px] truncate sm:max-w-[160px]">{displayName}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-20 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl ring-1 ring-slate-900/5"
          role="menu"
        >
          <div className="border-b border-slate-100 px-4 py-3">
            <p className="truncate text-sm font-medium text-slate-900">{displayName}</p>
            {user?.login && (
              <p className="truncate text-xs text-slate-500">{user.login}</p>
            )}
          </div>
          <div className="py-1">
            <Link
              to={`${BASE_URL}profile`}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 transition-colors hover:bg-slate-50"
              onClick={() => setOpen(false)}
              role="menuitem"
            >
              <User className="h-4 w-4 text-slate-400" />
              Profil
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-red-50 hover:text-red-700"
              role="menuitem"
            >
              <LogOut className="h-4 w-4 text-slate-400" />
              Déconnexion
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
